import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button } from '../../common';
import { Document } from '../../../gql/graphql';
import FolderTreeView from './FolderTreeView';
import DocumentDetailModal from './DocumentDetailModal';
import DocumentToolbar from './DocumentToolbar';
import { useAdminTranslation } from '../../../modules/i18n';

interface FolderTreeItem {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  parentId?: string;
  userId: string;
  subFolders?: FolderTreeItem[];
  documents?: Document[];
  _count?: {
    documents: number;
    subFolders: number;
  } | null;
}

interface DocumentsContentProps {
  documents: Document[];
  folders: any[];
  loading: boolean;
  error: string | null;
  onCreateDocument: () => void;
  onEditDocument: (document: Document) => void;
  onDeleteDocument: (documentId: string) => void;
  onDeleteDocuments: (documentIds: string[]) => Promise<boolean>;
  onDeleteSelection: (documentIds: string[], folderIds: string[]) => Promise<boolean>;
  onImportToFolder: (folderId: string) => void;
  onDeleteFolder: (folderIds: string[]) => Promise<boolean>;
  onUploadFolder: () => void;
  deletingDocumentIds: string[];
  deletingFolderIds: string[];
  selectedFolders: string[];
  setSelectedFolders: (folders: string[]) => void;
  selectedDocuments: string[];
  setSelectedDocuments: (ids: string[]) => void;
  loadFolderDocuments: (folderId: string) => Promise<void>;
  prefetchFolderDocuments: (folderId: string) => void;
  isFolderLoading: (folderId: string) => boolean;
  getFolderDocuments: (folderId: string) => Document[];
}

const DocumentsContent: React.FC<DocumentsContentProps> = React.memo(
  ({
    documents,
    folders,
    loading,
    error,
    onCreateDocument: _onCreateDocument,
    onEditDocument: _onEditDocument,
    onDeleteDocument,
    onDeleteDocuments,
    onDeleteSelection,
    onImportToFolder: _onImportToFolder,
    onDeleteFolder,
    onUploadFolder,
    deletingDocumentIds,
    deletingFolderIds,
    selectedFolders,
    setSelectedFolders,
    selectedDocuments,
    setSelectedDocuments,
    loadFolderDocuments,
    prefetchFolderDocuments,
    isFolderLoading,
    getFolderDocuments
  }) => {
    const { t } = useAdminTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentRecord, setCurrentRecord] = useState<Document | null>(null);

    // Build folder tree with lazy-loaded documents
    const folderTree = useMemo<FolderTreeItem[]>(() => {
      if (!folders) return [];

      // Create a map of folders by their IDs
      const folderMap = new Map<string, FolderTreeItem>();

      // Initialize all folders with lazy-loaded documents
      folders.forEach((f) => {
        folderMap.set(f.id, {
          ...f,
          subFolders: [],
          // Use lazy-loaded documents for this folder
          documents: getFolderDocuments(f.id)
        });
      });

      // Build hierarchy
      folders.forEach((f) => {
        if (f.parentId) {
          const parentFolder = folderMap.get(f.parentId);
          if (parentFolder) {
            parentFolder.subFolders?.push(folderMap.get(f.id)!);
          }
        }
      });

      // Get root folders
      return folders
        .filter((f) => !f.parentId)
        .map((f) => folderMap.get(f.id)!)
        .filter(Boolean);
    }, [folders, getFolderDocuments]);

    // Normalize helper for accent-insensitive search (Vietnamese friendly)
    const normalizeForSearch = (text: string) =>
      (text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'd')
        .replace(/\s+/g, ' ');

    // Debounce search input
    useEffect(() => {
      const id = setTimeout(() => setDebouncedQuery(normalizeForSearch(searchQuery.trim())), 250);
      return () => clearTimeout(id);
    }, [searchQuery]);

    // Filter tree with rules and compute temporary open states for visibility
    const { filteredTree, tempOpenIds } = useMemo(() => {
      if (!debouncedQuery) return { filteredTree: folderTree, tempOpenIds: new Set<string>() };

      const tempOpens = new Set<string>();

      const filterNode = (node: FolderTreeItem): FolderTreeItem | null => {
        const nameMatches =
          normalizeForSearch(node.name).includes(debouncedQuery) || normalizeForSearch(node.description || '').includes(debouncedQuery);
        const filteredDocs = (node.documents || []).filter((d) => normalizeForSearch(d.title).includes(debouncedQuery));
        const filteredSubs = (node.subFolders || []).map((sf) => filterNode(sf)).filter(Boolean) as FolderTreeItem[];

        // Rule 1: hide if nothing matches
        if (!nameMatches && filteredDocs.length === 0 && filteredSubs.length === 0) {
          return null;
        }

        // Rule 2: folder name matched → show node. Keep original children; no forced open state.
        if (nameMatches) {
          return {
            ...node
          };
        }

        // Rule 3: only children matched → show node with filtered children and mark it as open temporally
        tempOpens.add(node.id);
        return {
          ...node,
          documents: filteredDocs,
          subFolders: filteredSubs
        };
      };

      const result = folderTree.map((n) => filterNode(n)).filter(Boolean) as FolderTreeItem[];
      return { filteredTree: result, tempOpenIds: tempOpens };
    }, [folderTree, debouncedQuery]);

    const toggleFolder = async (folderId: string) => {
      const isCurrentlyOpen = openFolders[folderId];

      // Toggle the folder state
      setOpenFolders((prev) => ({
        ...prev,
        [folderId]: !prev[folderId]
      }));

      // If opening the folder and documents not loaded yet, load them
      if (!isCurrentlyOpen) {
        const folderDocs = getFolderDocuments(folderId);
        if (folderDocs.length === 0 && !isFolderLoading(folderId)) {
          // Load documents for this folder
          await loadFolderDocuments(folderId);
        }
      }
    };

    const handleFolderSelection = (id: string, checked: boolean) => {
      setSelectedFolders(checked ? [...selectedFolders, id] : selectedFolders.filter((fId) => fId !== id));
    };

    const _handleViewDocumentDetail = (document: Document) => {
      setSelectedDocument(document);
      setIsModalOpen(true);
    };

    const handleCloseModal = () => {
      setIsModalOpen(false);
      setSelectedDocument(null);
    };

    const showRenameModal = () => {
      // Placeholder for rename modal
      console.log('Show rename modal for:', currentRecord);
    };

    const showMoveFileModal = () => {
      // Placeholder for move file modal
      console.log('Show move file modal for:', currentRecord);
    };

    const handleRemoveFile = (documentIds: string[]) => {
      if (!documentIds || documentIds.length === 0) return;
      if (documentIds.length === 1) return onDeleteDocument(documentIds[0]);
      onDeleteDocuments(documentIds);
    };

    const allTopLevelFolderIds = useMemo(() => folderTree.map((f) => f.id), [folderTree]);
    const isAllSelected = allTopLevelFolderIds.length > 0 && allTopLevelFolderIds.every((id) => selectedFolders.includes(id));
    const toggleSelectAllFolders = () => {
      if (isAllSelected) {
        setSelectedFolders(selectedFolders.filter((id) => !allTopLevelFolderIds.includes(id)));
      } else {
        setSelectedFolders(Array.from(new Set([...selectedFolders, ...allTopLevelFolderIds])));
      }
    };

    // Sync selection with current document list (clear deleted IDs)
    useEffect(() => {
      const existingIds = new Set(documents.map((d) => d.id));
      const filtered = selectedDocuments.filter((id) => existingIds.has(id));
      if (filtered.length !== selectedDocuments.length) {
        setSelectedDocuments(filtered);
      }
    }, [documents, selectedDocuments, setSelectedDocuments]);

    // Keep selectedFolders in sync with existing folders list
    useEffect(() => {
      const folderIds = new Set(folders.map((f: any) => f.id));
      const filtered = selectedFolders.filter((id) => folderIds.has(id));
      if (filtered.length !== selectedFolders.length) {
        setSelectedFolders(filtered);
      }
    }, [folders, selectedFolders, setSelectedFolders]);

    // Helpers to compute recursive counts and membership
    const folderMap = useMemo(() => {
      const m = new Map<string, FolderTreeItem>();
      const walk = (nodes: FolderTreeItem[]) => {
        nodes.forEach((n) => {
          m.set(n.id, n);
          if (n.subFolders) walk(n.subFolders);
        });
      };
      walk(folderTree);
      return m;
    }, [folderTree]);

    const getDescendantDocIds = useCallback((folderId: string): string[] => {
      const node = folderMap.get(folderId);
      if (!node) return [];
      const list: string[] = [];
      const dfs = (f: FolderTreeItem) => {
        (f.documents || []).forEach((d) => list.push(d.id));
        (f.subFolders || []).forEach((sf) => dfs(sf));
      };
      dfs(node);
      return list;
    }, [folderMap]);

    const countDescendantFolders = useCallback((folderId: string): number => {
      const node = folderMap.get(folderId);
      if (!node) return 0;
      let count = 1; // include self
      const dfs = (f: FolderTreeItem) => {
        (f.subFolders || []).forEach((sf) => {
          count += 1;
          dfs(sf);
        });
      };
      dfs(node);
      return count;
    }, [folderMap]);

    // Compute counts for button label and filter document deletions
    const docIdsInsideSelectedFolders = useMemo(() => {
      const set = new Set<string>();
      selectedFolders.forEach((fid) => getDescendantDocIds(fid).forEach((id) => set.add(id)));
      return set;
    }, [selectedFolders, getDescendantDocIds]);

    const docsOnlySelected = useMemo(
      () => selectedDocuments.filter((id) => !docIdsInsideSelectedFolders.has(id)),
      [selectedDocuments, docIdsInsideSelectedFolders]
    );

    const docsFromFoldersCount = useMemo(() => {
      let total = 0;
      selectedFolders.forEach((fid) => {
        total += getDescendantDocIds(fid).length;
      });
      return total;
    }, [selectedFolders, getDescendantDocIds]);

    const foldersRecursiveCount = useMemo(() => {
      let total = 0;
      selectedFolders.forEach((fid) => {
        total += countDescendantFolders(fid);
      });
      return total;
    }, [selectedFolders, countDescendantFolders]);

    const totalDocsToDelete = docsOnlySelected.length + docsFromFoldersCount;
    const totalFoldersToDelete = foldersRecursiveCount;

    const handleRemoveSelected = () => {
      // Compute minimal folder set: exclude any folder whose ancestor is already selected
      const selectedSet = new Set(selectedFolders);
      const parentOf: Record<string, string | undefined> = {};
      folders.forEach((f: any) => (parentOf[f.id] = f.parentId));

      const isAncestorSelected = (fid: string): boolean => {
        let cur = parentOf[fid];
        while (cur) {
          if (selectedSet.has(cur)) return true;
          cur = parentOf[cur];
        }
        return false;
      };

      const minimalFolders = selectedFolders.filter((fid) => !isAncestorSelected(fid));

      // Remove documents that are inside any selected folder (they will be deleted by folder cascade)
      const docsToDelete = docsOnlySelected;

      onDeleteSelection(docsToDelete, minimalFolders);
    };

    if (loading) {
      return (
        <div className='flex h-64 items-center justify-center'>
          <div className='h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600'></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900'>
          <div className='flex'>
            <div className='text-sm text-red-700 dark:text-red-200'>{t('content.documents.errors.loadData')}: {error}</div>
          </div>
        </div>
      );
    }

    return (
      <div className='flex h-full flex-col gap-6'>
        {/* Header */}
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h2 className='text-lg font-medium text-gray-900 dark:text-dark-text'>{t('content.documents.title')}</h2>
            <p className='mt-1 text-sm text-gray-500 dark:text-dark-text-secondary'>{t('content.documents.description')}</p>
          </div>
        </div>

        {/* Toolbar: Search + Action Buttons */}
        <DocumentToolbar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          isAllSelected={isAllSelected}
          toggleSelectAllFolders={toggleSelectAllFolders}
          totalDocsToDelete={totalDocsToDelete}
          totalFoldersToDelete={totalFoldersToDelete}
          handleRemoveSelected={handleRemoveSelected}
          deletingDocumentIds={deletingDocumentIds}
          deletingFolderIds={deletingFolderIds}
          onUploadFolder={onUploadFolder}
          t={t}
        />

        {/* Folder Tree */}
        <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
          {filteredTree.length > 0 ? (
            <FolderTreeView
              folderTree={filteredTree}
              openFolders={
                debouncedQuery
                  ? {
                      ...openFolders,
                      ...Array.from(tempOpenIds).reduce((acc: Record<string, boolean>, id) => {
                        acc[id] = true;
                        return acc;
                      }, {})
                    }
                  : openFolders
              }
              selectedDocuments={selectedDocuments}
              selectedFolders={selectedFolders}
              onToggleFolder={toggleFolder}
              onFolderSelection={handleFolderSelection}
              onFolderDelete={onDeleteFolder}
              setCurrentRecord={setCurrentRecord}
              showRenameModal={showRenameModal}
              showMoveFileModal={showMoveFileModal}
              handleRemoveFile={handleRemoveFile}
              deletingDocumentIds={deletingDocumentIds}
              deletingFolderIds={deletingFolderIds}
              docIdsInsideSelectedFolders={Array.from(docIdsInsideSelectedFolders)}
              onDocumentSelection={(id, checked) =>
                setSelectedDocuments(checked ? [...selectedDocuments, id] : selectedDocuments.filter((d) => d !== id))
              }
              searchTerm={debouncedQuery}
              sortDirection={sortDirection}
              isFolderLoading={isFolderLoading}
              onFolderHover={prefetchFolderDocuments}
            />
          ) : (
            <div className='py-12 text-center'>
              <svg className='mx-auto h-12 w-12 text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
                <path
                  vectorEffect='non-scaling-stroke'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z'
                />
              </svg>
              <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-dark-text'>{t('content.documents.empty.noFolders')}</h3>
              <p className='mt-1 text-sm text-gray-500 dark:text-dark-text-secondary'>{t('content.documents.empty.uploadFirstFolder')}</p>
              <div className='mt-6'>
                <Button onClick={onUploadFolder}>{t('content.documents.buttons.uploadFolder')}</Button>
              </div>
            </div>
          )}
        </div>

        {/* Document Detail Modal */}
        {isModalOpen && selectedDocument && <DocumentDetailModal isOpen={isModalOpen} onClose={handleCloseModal} document={selectedDocument} />}
      </div>
    );
  }
);

// Add displayName for debugging purposes
DocumentsContent.displayName = 'DocumentsContent';

export default DocumentsContent;